"use client";

import { getSectionDefaultProps } from "@/Templates/Doccure/getSectionDefaultProps";
import { FC, ReactElement } from "react";

export type GeneratedSectionRenderer<P> = (props: P) => ReactElement;

export function buildGeneratedSectionProps<T extends Record<string, unknown>>(
  sectionKey: string,
  overrides?: Partial<T>,
): T {
  return getSectionDefaultProps<T>(sectionKey, {
    ...(overrides ?? {}),
  } as Partial<T>);
}

export function createGeneratedRenderer<P, T extends Record<string, unknown>>(
  Component: FC<T>,
  sectionKey: string,
  overrides?: Partial<T>,
): GeneratedSectionRenderer<P> {
  // eslint-disable-next-line react/display-name
  return (_props: P) => (
    <Component {...buildGeneratedSectionProps<T>(sectionKey, overrides)} />
  );
}

export function renderGeneratedSection<P>(
  props: P,
  renderers: Record<string, GeneratedSectionRenderer<P>>,
  defaultAppearanceKey: string,
  getAppearanceKey?: (props: P) => string | null | undefined,
): ReactElement {
  const appearanceKey = getAppearanceKey?.(props) ?? defaultAppearanceKey;
  const fallbackRenderer =
    renderers[defaultAppearanceKey] ?? Object.values(renderers)[0];
  const renderer = renderers[appearanceKey] ?? fallbackRenderer;

  if (!renderer) {
    throw new Error(
      `Generated renderer not found for appearance: ${appearanceKey}`,
    );
  }

  return renderer(props);
}
